import React, { useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Divider } from 'antd';
import { useTranslation } from '@packages/utils';
import Button from 'components/Base/Button/Button';
import { SelectWithAddProps } from './SelectWithAdd.types';
import {
  SDropdownContainer,
  SSelect,
  SDropdownContent,
  SEmptyState,
} from './SelectWithAdd.styles';

export const SelectWithAdd: React.FC<SelectWithAddProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select option',
  onAdd,
  addButtonLabel,
  emptyOptionsLabel,
  disabled = false,
  'data-cy': dataCy,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { t } = useTranslation({
    keyPrefix: 'descriptions.generic-components.select-with-add',
  });

  const defaultAddButtonLabel =
    addButtonLabel || t('ADD_NEW', { defaultValue: 'Add New' });
  const defaultEmptyLabel =
    emptyOptionsLabel || t('NO_ITEMS', { defaultValue: 'No items' });

  const dropdownRender = (menu: React.ReactElement) => (
    <SDropdownContent>
      {options.length === 0 ? (
        <SEmptyState>{defaultEmptyLabel}</SEmptyState>
      ) : (
        menu
      )}
      {onAdd && (
        <>
          {options.length > 0 && <Divider style={{ margin: 0 }} />}
          <Button
            variant="default"
            icon={<PlusOutlined />}
            onClick={e => {
              e.preventDefault();
              setDropdownOpen(false);
              onAdd();
            }}
            fullWidth
            style={{ justifyContent: 'center', padding: '8px 12px' }}
          >
            {defaultAddButtonLabel}
          </Button>
        </>
      )}
    </SDropdownContent>
  );

  return (
    <SDropdownContainer>
      <SSelect
        value={value}
        onChange={selectedValue => onChange?.(selectedValue as string)}
        placeholder={placeholder}
        options={options}
        disabled={disabled}
        dropdownRender={dropdownRender}
        open={dropdownOpen}
        onDropdownVisibleChange={setDropdownOpen}
        data-cy={dataCy}
        showSearch
        filterOption={(input, option) =>
          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
        }
      />
    </SDropdownContainer>
  );
};

export default SelectWithAdd;
